import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FloatingLanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';

export default function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error(t('reset_password.toasts.fill_fields'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('reset_password.toasts.mismatch'));
      return;
    }
    // pantallas-alumno (G), firma 3 de la parte 2: la contrasena nueva tambien pide 8
    if (password.length < 8) {
      toast.error(t('reset_password.toasts.too_short'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success(t('reset_password.toasts.success'));
    } catch (err) {
      const detail = err.response?.data?.detail || t('reset_password.toasts.reset_failed_fallback');
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0e0e0e]">
      <FloatingLanguageSelector />
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-block mb-8 font-['Space_Grotesk'] font-bold text-xl tracking-tight">
            Quiz<span className="text-[#FF6B00]">essa</span>
          </Link>
          <h1 className="text-3xl font-bold text-white" data-testid="reset-password-title">{t('reset_password.title')}</h1>
          <p className="text-[#6a6a6a] mt-2">{t('reset_password.subtitle')}</p>
        </div>

        {success ? (
          <div className="space-y-6 text-center" data-testid="reset-success-state">
            <div className="flex flex-col items-center gap-4 p-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5">
              <CheckCircle2 className="w-12 h-12 text-emerald-400" />
              <h2 className="text-xl font-semibold text-white">{t('reset_password.success.title')}</h2>
              <p className="text-[#6a6a6a] text-sm">{t('reset_password.success.body')}</p>
            </div>
            <Button onClick={() => navigate('/login')} className="w-full bg-[#FF6B00] hover:bg-[#FF8c3e] text-white font-semibold" data-testid="reset-go-to-login-btn">
              {t('reset_password.success.go_to_login_btn')}
            </Button>
          </div>
        ) : error ? (
          <div className="space-y-6 text-center" data-testid="reset-error-state">
            <div className="flex flex-col items-center gap-4 p-8 rounded-2xl border border-red-500/20 bg-red-500/5">
              <XCircle className="w-12 h-12 text-red-400" />
              <h2 className="text-xl font-semibold text-white">{t('reset_password.error.title')}</h2>
              <p className="text-[#6a6a6a] text-sm">{error}</p>
            </div>
            <Button onClick={() => navigate('/forgot-password')} className="w-full bg-[#FF6B00] hover:bg-[#FF8c3e] text-white font-semibold" data-testid="reset-request-new-btn">
              {t('reset_password.error.request_new_btn')}
            </Button>
            <Link to="/login" className="block text-[#6a6a6a] hover:text-white transition text-sm" data-testid="reset-back-link">
              {t('reset_password.error.back_to_login_btn')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#cccccc]">{t('reset_password.form.new_label')}</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6a6a]" />
                <Input
                  id="password" type={showPassword ? "text" : "password"} name="new_password" autoComplete="new-password"
                  placeholder={t('reset_password.form.new_placeholder')}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-[#181818] border-[#2a2a2a] text-white"
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a6a6a] hover:text-white" data-testid="reset-toggle-password">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#cccccc]">{t('reset_password.form.confirm_label')}</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6a6a]" />
                <Input
                  id="confirmPassword" type={showPassword ? "text" : "password"} name="new_password_confirm" autoComplete="new-password"
                  placeholder={t('reset_password.form.confirm_placeholder')}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-[#181818] border-[#2a2a2a] text-white"
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-[#FF6B00] hover:bg-[#FF8c3e] text-white font-semibold"
              data-testid="reset-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('reset_password.form.submit_btn')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
